import { LngLatBounds } from 'mapbox-gl';
import {
  BREAKPOINT_WIDTH__TABLET,
  DOM_ELEMENT_DIMENSIONS__PAGE_HOLDER_STICKY_HEADER,
  MAP_PADDING__DEFAULT,
} from '../../configurations/constants';
import { checkClientToken, telemetry } from '../backend';
import { captureEvent } from '../event-capture';
import { definitions, emit, subscribe } from '../message-broker';
import { getStore } from '../store';

export const shiftLatLonByPixelRelativeToMapContainer = (
  mapReference,
  latitude,
  longitude,
  xPixels,
  yPixels,
) => {
  const projectedLatLon = mapReference.project([longitude, latitude]);
  const shiftedLatLon = {
    x: projectedLatLon.x + xPixels,
    y: projectedLatLon.y + yPixels,
  };
  return mapReference.unproject(shiftedLatLon);
};

/**
 * Fit the line layer to the map bounds with optional padding
 *  ref: https://stackoverflow.com/questions/35586360/mapbox-gl-js-getbounds-fitbounds
 *
 * @param {Object} mapReference - Map object containing methods and properties to interact with map
 * @param {Array} coordinates -  Array of [longitude, latitude] arrays [[number, number]]
 * @param {number | Object} padding - {top: number, bottom: number, left: number, right: number}
 * @param {Object} pointOffset - map center offset {xOffset: number, yOffset: number}
 * @param {Object} fallbackMapCenter - Used as the fallback center point {lat: number, lon: number}
 * @param {number} mapPitch - Optional map pitch change (0 - 60)
 * @param {number} mapBearing - Optional map bearing change
 * @return {void} Nothing
 */
export const fitMapToLineLayerBounds = (
  mapReference,
  coordinates,
  padding,
  pointOffset = undefined,
  fallbackMapCenter = undefined,
  mapPitch = undefined,
  mapBearing = undefined,
) => {
  try {
    const lineStringBounds = coordinates.reduce(
      (bounds, coordinate) => bounds.extend(coordinate),
      new LngLatBounds(coordinates[0], coordinates[0]),
    );

    // NOTE: works best when using padding to leave `offset` blank and just supply padding
    mapReference.fitBounds(lineStringBounds, {
      linear: true,
      padding,
      [mapPitch !== undefined && 'pitch']: mapPitch,
      [mapBearing !== undefined && 'bearing']: mapBearing,
    });
  } catch {
    // If any failures in shifting to center of trip, fall back to first point
    if (fallbackMapCenter?.lat && fallbackMapCenter?.lon) {
      const shiftedLatLon = shiftLatLonByPixelRelativeToMapContainer(
        mapReference,
        fallbackMapCenter.lat,
        fallbackMapCenter.lon,
        pointOffset?.xOffset || 0,
        pointOffset?.yOffset || 0,
      );
      mapReference.easeTo({ center: shiftedLatLon });
    }
  }
};

/**
 * This is used to calculate the padding given to mapbox when fitting layers on the map
 *
 * @param {Object} domElementDimensions - dimensions of the dom element to pad
 * @param {boolean} includeHeader - indicates whether to include the page header in the calculations
 * @param {number} mapOffset - size of the map offset to take into account in pixels
 */
export const calculateMapPadding = (
  domElementDimensions = undefined,
  includeHeader = true,
  mapOffset = 0,
  allowForMarkers = false,
) => {
  // Setup the width & padding config
  const isMobileWidth = window.innerWidth < BREAKPOINT_WIDTH__TABLET;
  const defaultPadding = isMobileWidth
    ? MAP_PADDING__DEFAULT.mobileWidth
    : MAP_PADDING__DEFAULT.defaultWidth;

  // setup the dom element dimensions
  let topHeaderHeight = 0;
  let domElementToPadWidth = 0;
  let domElementToPadHeight = 0;
  if (includeHeader) {
    topHeaderHeight = isMobileWidth
      ? DOM_ELEMENT_DIMENSIONS__PAGE_HOLDER_STICKY_HEADER.mobile.height
      : DOM_ELEMENT_DIMENSIONS__PAGE_HOLDER_STICKY_HEADER.default.height;
  }
  if (isMobileWidth) {
    domElementToPadWidth = domElementDimensions?.mobile?.width || 0;
    domElementToPadHeight = domElementDimensions?.mobile?.height || 0;
  } else {
    domElementToPadWidth = domElementDimensions?.default?.width || 0;
    domElementToPadHeight = domElementDimensions?.default?.height || 0;
  }
  // Replace 100% with relevant window dimension
  if (domElementToPadWidth === '100%')
    domElementToPadWidth = isMobileWidth ? window.innerWidth : 0;
  if (domElementToPadHeight === '100%') domElementToPadHeight = 0;

  // get our safe area heights
  const safeAreaTop = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--sat') || 0,
    10,
  );
  const safeAreaBottom = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--sab') || 0,
    10,
  );

  // Setup the mobile layout map padding
  let paddingTop = defaultPadding + safeAreaTop + topHeaderHeight;
  let paddingBottom = defaultPadding + +domElementToPadHeight + safeAreaBottom;
  let paddingLeft = defaultPadding;
  let paddingRight = defaultPadding;

  // Tablet & above layout map padding
  if (!isMobileWidth) {
    let topElementPadding = domElementToPadHeight;
    if (domElementToPadWidth === '100%')
      topElementPadding = +domElementToPadHeight / 2;
    paddingTop = defaultPadding + safeAreaTop + topElementPadding;
    paddingBottom = defaultPadding + safeAreaBottom;
    paddingLeft = defaultPadding + +domElementToPadWidth / 2;

    // Account for the map offset
    if (mapOffset && mapOffset > 0) {
      paddingRight = defaultPadding + mapOffset;
    } else {
      paddingRight = defaultPadding;
    }
  }

  if (allowForMarkers) {
    paddingTop += 57;
    paddingLeft += 25;
    paddingRight += 25;
  }

  const mapPadding = {
    top: Math.round(paddingTop),
    bottom: Math.round(paddingBottom),
    left: Math.round(paddingLeft),
    right: Math.round(paddingRight),
  };

  // Point offsets for fallback
  const xOffset = isMobileWidth ? 0 : (paddingLeft / 2) * -1;
  const yOffset = isMobileWidth
    ? paddingBottom / 2 - paddingTop / 2
    : (paddingTop / 2) * -1;
  const pointOffset = {
    xOffset: Math.round(xOffset),
    yOffset: Math.round(yOffset),
  };

  return { mapPadding, pointOffset };
};

export const moveMapToFloatPlanTrip = (mapReference, coordinates, padding) => {
  if (mapReference && coordinates && coordinates.length > 0 && padding) {
    try {
      const bounds = new LngLatBounds();
      coordinates.forEach((coordinate) => {
        bounds.extend(coordinate);
      });
      mapReference.fitBounds(bounds, { padding });
    } catch {
      // This is to prevent an white screen issue in Android 14 - API/SDK 34
      // in this scenario the side effect it wont be fitting bounds the first time we add a waypoint.
      // subsecuent waypoints will work as expected.
    }
  }
};

export const captureAppStartEvent = async () => {
  // Documentation for this data capture: https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-device/index.html

  // eslint-disable-next-line no-undef
  const deviceInfo = typeof device !== 'undefined' ? device : null;
  const { currentMember } = getStore().getState().backendService;

  await captureEvent({
    type: 'app-start: device-information',
    payload: {
      /* eslint-disable no-undef */
      model: deviceInfo?.model,
      platform: deviceInfo?.platform ?? 'Browser',
      version: deviceInfo?.version,
      manufacturer: deviceInfo?.manufacturer,
      isVirtual: deviceInfo?.isVirtual, // Not sure if we want this? Would be VERY rare if its virtual
      serialNumber: deviceInfo?.serial,
      /* eslint-enable no-undef */
      isLoggedIn: !!currentMember,
    },
    sendToAmplitude: true,
  });
};

/* eslint-disable-next-line import/prefer-default-export */
export const initialize = async ({ bootstrapperService, history }) => {
  // Initially check the client token on load.
  checkClientToken();

  /**
   *
   * Listen to events and emit them to the message broker.
   *
   */
  if (getStore().getState().configurations.runningEnvironment === 'capacitor') {
    bootstrapperService.onResume(() => {
      emit(definitions.window.resume);
    });

    bootstrapperService.onPause(() => {
      emit(definitions.window.blur, true);
    });
  } else {
    window.addEventListener('focus', () => {
      emit(definitions.window.focus, true);
    });

    window.addEventListener('blur', () => {
      emit(definitions.window.blur, true);
    });
  }

  window.onerror = (message, url, lineNumber) => {
    emit(definitions.window.error, {
      message,
      url,
      lineNumber,
    });
    // Reload the app, we need to in order to avoid the white screen of death.
    // keywords to find this when working locally: debug reload restart error reloader crash
    // Turn off when NOT in production mode
    if (!process.env.NODE_ENV === 'production') {
      setTimeout(() => {
        window.location.reload(true);
      }, 1000);
    }
  };

  window.onbeforeunload = () => {
    emit(definitions.window.unload, 'unloaded');
    return null;
  };

  history.listen((location) => {
    emit(definitions.router.locationChange, location);
  });

  // Back button logic for android
  document.addEventListener(
    'backbutton',
    (event) => {
      event.preventDefault();
      history.goBack();
    },
    false,
  );

  subscribe(definitions.router.locationChange, async (location) => {
    const { origin } = window.location;
    telemetry(
      telemetry.DEBUG,
      `[utilities.history] current url location: ${location.pathname}${location.search}`,
    );
    await captureEvent({
      type: 'pageview',
      payload: {
        page: {
          url: origin + location.pathname + location.search,
        },
      },
      customEvent: false,
    });
  });
  subscribe(definitions.authentication.stateChange, async (authenticated) => {
    telemetry(
      telemetry.DEBUG,
      `[utilities.authentication.statechange] authentication status: ${authenticated}`,
    );

    if (authenticated) {
      // Don't await as it doesn't have to happen synchronously, happy for it to happen when it happens
      await captureEvent({
        type: 'login: member-login',
        payload: {},
        // At this point in time, the login event gets sent on authentication state change which fires more often than anticipated. It does not reflect a true login event where a user enters their email.
        sendToAmplitude: false,
      });

      // // Check for an active trip when the member is authenticated
      // getStore().dispatch(loadActiveTrip());
    }
  });

  // Check client token on window focus
  subscribe(definitions.window.focus, () => {
    // Whenever we refocus on the current window, check the member with the server
    checkClientToken();
  });
};
